import { gql } from 'graphql-tag';

export const createSong = gql`
  mutation CreateSong(
    $title: String!
    $artist: String
    $bpm: Int
    $trackNumber: Int
  ) {
    createSong(
      input: {
        title: $title
        artist: $artist
        trackNumber: $trackNumber
        bpm: $bpm
      }
    ) {
      title
      artist
      id
      bpm
      trackNumber
    }
  }
`;

export const createPlaylist = gql`
  mutation CREATE_PLAYLIST($input: PlaylistCreateInput!) {
    playlist(input: $input) {
      id
      title
      description
      albumArtworkUrl
      songs {
        id
      }
    }
  }
`;

export const removePlaylist = gql`
  mutation REMOVE_PLAYLIST($id: String!) {
    playlistRemove(id: $id)
  }
`;

export const updatePlaylist = gql`
  mutation UPDATE_PLAYLIST($input: PlaylistUpdateInput!) {
    playlistUpdate(input: $input) {
      id
      title
      description
      albumArtworkUrl
      includeDescriptionWithShare
      songs {
        id
        title
        artist
        bpm
        trackNumber
        albumArtworkUrl
        shareLinkCreatedAt
        shareLink
        files {
          title
          optimizedUrl
          id
          active
          notes
        }
      }
    }
  }
`;

export const deleteSong = gql`
  mutation DeleteSong($id: String!) {
    deleteSong(id: $id) {
      success
      message
    }
  }
`;

export const deleteFile = gql`
  mutation DeleteFile($id: String!) {
    deleteFile(id: $id) {
      success
      message
    }
  }
`;

export const createFile = gql`
  mutation CreateFile(
    $title: String!
    $songId: String!
    $notes: String
    $originalFilename: String!
  ) {
    createFile(
      input: {
        title: $title
        songId: $songId
        originalFilename: $originalFilename
        notes: $notes
      }
    ) {
      file {
        id
      }
      uploadUrl
    }
  }
`;

export const retryFile = gql`
  mutation RetryFile($id: String!) {
    retryFile(id: $id) {
      file {
        id
      }
      uploadUrl
    }
  }
`;

export const createArtworkUploadUrl = gql`
  mutation CreateArtworkUploadUrl($id: ID!, $filename: String!) {
    createSongArtworkUploadUrl(input: { id: $id, filename: $filename }) {
      artworkUploadUrl
    }
  }
`;

export const createPlaylistArtworkUploadUrl = gql`
  mutation CreatePlaylistArtworkUploadUrl($id: ID!, $filename: String!) {
    createPlaylistArtworkUploadUrl(input: { id: $id, filename: $filename }) {
      artworkUploadUrl
    }
  }
`;

export const updateSong = gql`
  mutation UpdateSong(
    $id: ID!
    $title: String
    $bpm: Int
    $artist: String
    $notes: String
    $songKey: String
    $trackNumber: Int
    $removeArtwork: Boolean
    $lyrics: String
    $about: String
    $releaseDate: String
    $options: OptionsInput
    $primaryHex: String
    $secondaryHex: String
  ) {
    updateSong(
      input: {
        id: $id
        title: $title
        bpm: $bpm
        artist: $artist
        trackNumber: $trackNumber
        removeArtwork: $removeArtwork
        songKey: $songKey
        notes: $notes
        lyrics: $lyrics
        about: $about
        releaseDate: $releaseDate
        options: $options
        primaryHex: $primaryHex
        secondaryHex: $secondaryHex
      }
    ) {
      id
      title
      artist
      bpm
      trackNumber
      albumArtworkUrl
      songKey
      notes
      files {
        title
        optimizedUrl
        id
        notes
      }
      about
      lyrics
      releaseDate
      defaultArtwork
      primaryHex
      secondaryHex
      options {
        allowReactions
        showAbout
        showReleaseDate
        showLyrics
        showAudioAnalysis
        aiAudioAnalysis
        aiLyrics
      }
    }
  }
`;

export const setSongActiveVersion = gql`
  mutation SetSongActiveVersion($songId: String!, $fileId: String!) {
    setSongActiveVersion(input: { songId: $songId, fileId: $fileId }) {
      success
      message
    }
  }
`;

export const activateSongShareLink = gql`
  mutation ActivateSongShareLink($songId: String!, $access: ShareLinkAccess) {
    activateSongShareLink(input: { songId: $songId, access: $access }) {
      success
      message
    }
  }
`;

export const deactivateSongShareLink = gql`
  mutation DeactivateSongShareLink($songId: String!) {
    deactivateSongShareLink(songId: $songId) {
      success
      message
    }
  }
`;

export const updateSongShareLinkAccess = gql`
  mutation UpdateSongShareLinkAccess(
    $songId: String!
    $access: ShareLinkAccess
  ) {
    updateSongShareLinkAccess(input: { songId: $songId, access: $access }) {
      success
      message
    }
  }
`;

export const activatePlaylistShareLink = gql`
  mutation ActivatePlaylistShareLinK(
    $playlistId: String!
    $access: ShareLinkAccess
  ) {
    activatePlaylistShareLink(
      input: { playlistId: $playlistId, access: $access }
    ) {
      success
      message
    }
  }
`;

export const deactivatePlaylistShareLink = gql`
  mutation DeactivatePlaylistShareLinK($playlistId: String!) {
    deactivatePlaylistShareLink(playlistId: $playlistId) {
      success
      message
    }
  }
`;

export const updatePlaylistShareLinkAccess = gql`
  mutation UpdatePlaylistShareLinkAccess(
    $playlistId: String!
    $access: ShareLinkAccess
  ) {
    updatePlaylistShareLinkAccess(
      input: { playlistId: $playlistId, access: $access }
    ) {
      success
      message
    }
  }
`;

export const updateFile = gql`
  mutation UpdateFile(
    $id: ID!
    $title: String
    $notes: String
    $bpm: Float
    $key: String
    $keyScale: String
  ) {
    updateFile(
      input: {
        id: $id
        title: $title
        notes: $notes
        bpm: $bpm
        key: $key
        keyScale: $keyScale
      }
    ) {
      id
      title
      active
      status
      optimizedUrl
      notes
      bpm
      bpmCalculationStatus
      key
      keyScale
      keyCalculationStatus
    }
  }
`;

export const getCheckoutUrl = gql`
  mutation GetCheckoutUrl($subscriptionType: SubscriptionType!) {
    getCheckoutUrl(input: { subscriptionType: $subscriptionType }) {
      sessionUrl
    }
  }
`;

export const getBillingPortalSession = gql`
  mutation GetBillingPortalSession {
    getBillingPortalSession {
      sessionUrl
    }
  }
`;

export const cancelSubscription = gql`
  mutation CancelSubscription {
    cancelSubscription {
      status
    }
  }
`;

export const userClickEvent = gql`
  mutation UserClickEvent($input: UserClickInput!) {
    userClickEvent(input: $input) {
      success
    }
  }
`;

export const requestAssistantRecalculation = gql`
  mutation RequestAssistantRecalculation(
    $fileId: String!
    $action: AssistantAction!
  ) {
    requestAssistantRecalculation(fileId: $fileId, action: $action) {
      success
      message
    }
  }
`;

export const dismissMarketingUpdate = gql`
  mutation DismissMarketingUpdate($marUpdateId: String!) {
    dismissMarketingUpdate(marUpdateId: $marUpdateId) {
      success
    }
  }
`;
